<template>
  <div
    :class="{
      '-ml-[15px] mb-1': !props.grid
    }"
  >
    <div v-if="props.node">
      <label
        v-if="props.showCheckbox"
        class="flex items-center cursor-pointer"
        :class="{
          'justify-center': props.grid,
          'space-x-2': !props.grid
        }"
      >
        <div
          :class="[
            'aspect-square relative flex items-center justify-center w-5 h-5 border-[2px] border-border-divider rounded-[4px]',
            isSelected(props.node.key)
              ? 'bg-transparent border-transparent'
              : 'bg-white',
            ,
            props.grid ? 'hidden' : ''
          ]"
        >
          <svg
            v-if="isSelected(props.node.key)"
            class="absolute left-[-2px] top-[-2px] w-5 h-5 pointer-events-none outline-none"
            viewBox="0 0 21 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 0.5C19.6406 0.5 21 1.85938 21 3.5V18.5C21 20.1875 19.6406 21.5 18 21.5H3C1.3125 21.5 0 20.1875 0 18.5V3.5C0 1.85938 1.3125 0.5 3 0.5H18ZM15.8906 8.9375C16.4062 8.42188 16.4062 7.625 15.8906 7.10938C15.375 6.59375 14.5781 6.59375 14.0625 7.10938L9 12.1719L6.89062 10.1094C6.375 9.59375 5.57812 9.59375 5.0625 10.1094C4.54688 10.625 4.54688 11.4219 5.0625 11.9375L8.0625 14.9375C8.57812 15.4531 9.375 15.4531 9.89062 14.9375L15.8906 8.9375Z"
              fill="black"
            />
          </svg>
        </div>
        <input
          type="checkbox"
          class="hidden"
          :checked="isSelected(props.node.key)"
          @change="toggleSelection(props.node.key, props.node.name)"
        />
        <div v-if="props.grid">
          <div
            v-if="getThumbnail(props.node.name)"
            :class="
              twJoin(
                'border-2 border-transparent rounded-full overflow-hidden w-12 h-12',
                isSelected(props.node.key) && '!border-primary-tangerine'
              )
            "
          >
            <img
              :alt="props.node.name?.toLowerCase()"
              :src="`${config.public.mediaUrl}${getThumbnail(props.node.name) as string}`"
              class="w-full h-full object-cover"
            />
          </div>
          <div
            v-else
            :class="
              twJoin(
                'border-2 border-transparent rounded-full overflow-hidden w-12 h-12',
                isSelected(props.node.key) && '!border-primary-tangerine'
              )
            "
          />
          <div class="text-sm text-center mt-2">{{ props.node.name }}</div>
        </div>
        <div v-else class="font-normal">{{ props.node.name }}</div>
      </label>

      <div
        v-if="
          props.node.children && Object.keys(props.node.children).length > 0
        "
        :class="{
          'grid grid-cols-4 justify-center items-center lg:grid-cols-3 gap-y-3':
            props.grid
        }"
        class="mt-1"
      >
        <div
          v-for="(child, key) in props.node.children"
          :key="key"
          :class="{
            'ml-8': !props.grid
          }"
        >
          <ProductListFacetTreeNode
            :type="props.type"
            :node="child"
            :show-checkbox="true"
            :grid="props.grid"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { find } from 'lodash-es'
import type { LocationQuery } from 'vue-router'
import { twJoin } from 'tailwind-merge'
import { useProductFilterStore } from '~/stores/productFilter'

const props = defineProps<{
  node: FacetNode
  showCheckbox: boolean
  grid?: boolean
  type: string
}>()

const route = useRoute()
const config = useRuntimeConfig()
const { locale } = useI18n()
const state = useProductFilterStore()
const { toggle, add } = useProductFilterStore()
const { selectedValues, category } = storeToRefs(state)

const toggleSelection = (key: string, name: string) => {
  toggle({ type: props.type, value: key, name: name })
}

const isSelected = (key: string) => {
  return !!find(
    selectedValues.value,
    (value) => value.type === props.type && value.value === key
  )
}

const getThumbnail = (name: string) => {
  const found = find(category.value, (category) => {
    return locale.value === 'en'
      ? category.name_en === name
      : category.name_th === name || category.name_en === name
  })

  return found?.thumbnail_image_url
}

interface Filter {
  type: string
  value: string
  name: string
}

function convertQueryToFilters(
  queryObject: Record<string, string | string[]>
): Filter[] {
  const reverseType = (type: string): string => {
    // Add "category_" prefix and "_en" suffix back
    return `category_${type}_en`
  }

  const filters: Filter[] = []

  // Iterate through the query object
  for (const [key, value] of Object.entries(queryObject)) {
    const type = reverseType(key) // Reverse the transformation of the type

    // Handle both single values and arrays of values
    if (Array.isArray(value)) {
      value.forEach((val) => {
        filters.push({
          type: type,
          value: `${key} > ${val}`,
          name: val
        })
      })
    } else {
      filters.push({
        type: type,
        value: `${key} > ${value}`,
        name: value
      })
    }
  }

  return filters
}

const handleUpdateQuery = (newQuery: LocationQuery, fromRoute?: boolean) => {
  const query = convertQueryToFilters(
    Object.fromEntries(
      Object.entries(newQuery)
        .filter(([_, value]) => value !== null)
        .map(([key, value]) => [key, value as string | string[]])
    )
  )

  const target = find(query, (item) => item.name === props.node.name)
  if (target) {
    if (!fromRoute) {
      toggle({ type: props.type, value: props.node.key, name: props.node.name })
    } else {
      add({ type: props.type, value: props.node.key, name: props.node.name })
    }
  }
}

onMounted(() => {
  handleUpdateQuery(route.query)
})
</script>
